import {FC, useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';
import classNames from 'classnames';
import {getCard} from '../../../api/dashboard';
import Spinner from '../../common/Spinner';
import {CardType, getRefreshTime, cardCodeToRoute} from '../../../constants/dashboard';
import {useSnackbarHook} from '../../../hooks/useSnackbar';

type CardProps = {
  code: CardType;
  count?: number;
};

const Card: FC<CardProps> = ({code, count: initialCount}) => {
  const {t} = useTranslation('dashboard');
  const history = useHistory();
  const [count, setCount] = useState(initialCount);
  const [isLoading, setIsLoading] = useState(false);
  const {addErrorToast} = useSnackbarHook();

  const refreshTime = useMemo(() => getRefreshTime(code), [code]);
  const redirectParams = cardCodeToRoute[code];

  const loadCard = useCallback(async () => {
    setIsLoading(true);

    try {
      const {count: newCount} = await getCard(code);

      setCount(newCount);
    } catch (error) {
      addErrorToast(error?.message);
    } finally {
      setIsLoading(false);
    }
  }, [code]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadCard();
    }, refreshTime);

    return () => clearInterval(interval);
  }, [loadCard, refreshTime]);

  const handleClick = useCallback(() => {
    const {route, state} = redirectParams || {};

    if (!route) return;

    if (state) {
      return history.push(route, state);
    }

    if (redirectParams) {
      history.push(route);
    }
  }, [redirectParams]);

  return (
    <article
      className={classNames('card', {'active': !!redirectParams})}
      title={t(`cardNames.${code}`)}
      onClick={handleClick}
    >
      <h3 className="card__title">{t(`cardNames.${code}`)}</h3>
      <div className="card__value">
        {isLoading ? <Spinner embedded /> : count}
      </div>
    </article>
  );
};

export default Card;
